// The change queue's file uploads at replay (BL-2345, BL-2447, ADR 066 — Offline Mode § 4.6): an
// add's staged poster, then its file, read back from the device and sent through the signed-URL flow,
// and what a failed upload means for its entry (BUG-2070).
import type { ChangeQueueCall, ChangeQueuePayload } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import type { ReplayOutcome } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-outcome';
import type { OfflineMediaStaging } from '../../gateways/native-device';
import type { ReplayTransport, ReplayUploadResult } from './replay-transport';

/** What an upload needs from the replay's deps. */
export interface UploadDeps {
  media: OfflineMediaStaging | null;
  transport: ReplayTransport;
  logError?: (message: string, err: Error) => void;
}

const FILE_GONE = 'The file for this change is no longer on this device.';

/**
 * @param body - a queued call's body.
 * @returns the body as an object, or an empty one when it is not an object.
 */
export function asBody(body: ChangeQueueCall['body']): Record<string, ChangeQueueCall['body']> {
  return typeof body === 'object' && body !== null && !Array.isArray(body) ? body : {};
}

/**
 * Upload a document add's staged file.
 *
 * @param deps - the staged files, the transport and the error log.
 * @param payload - the add's payload.
 * @returns the storage path to merge into the add's body, or the entry's outcome.
 * @throws on a network failure (the pass stops and the entry stays queued).
 * @sideEffect Uploads the staged file to object storage.
 */
export async function uploadFiles(
  deps: UploadDeps,
  payload: ChangeQueuePayload,
): Promise<{ ok: true; paths: Record<string, string> } | { ok: false; outcome: ReplayOutcome }> {
  const uploaded = await uploadStaged(deps, payload);
  if (!uploaded.ok) return uploaded;
  return { ok: true, paths: { storagePath: uploaded.storagePath } };
}

/**
 * An add with no staged file whose body already names its storage object.
 *
 * @param payload - the queued add's payload.
 * @returns true when there is nothing to upload.
 */
export function alreadyStored(payload: ChangeQueuePayload): boolean {
  const path = asBody(payload.call.body).storagePath;
  return payload.stagedFileId == null && typeof path === 'string' && path.length > 0;
}

/**
 * @param message - the reason, as the Change queue tab shows it.
 * @returns a `failed` outcome carrying it.
 */
export function failure(message: string): ReplayOutcome {
  return { kind: 'failed', error: { message, code: null, httpStatus: null, fieldNames: [] } };
}

/** Read one staged file and upload it; one at a time, so a large video is never held twice. */
async function uploadStaged(
  deps: UploadDeps,
  payload: ChangeQueuePayload,
): Promise<{ ok: true; storagePath: string } | { ok: false; outcome: ReplayOutcome }> {
  const { stagedFileId, call } = payload;
  const lotId = call.params.id;
  if (!deps.media || stagedFileId == null || !lotId) return { ok: false, outcome: failure(FILE_GONE) };
  let staged: Awaited<ReturnType<OfflineMediaStaging['read']>>;
  try {
    staged = await deps.media.read(stagedFileId);
  } catch (err) {
    deps.logError?.('change queue: a staged file could not be read', err instanceof Error ? err : new Error(String(err)));
    return { ok: false, outcome: failure(FILE_GONE) };
  }
  if (!staged) return { ok: false, outcome: failure(FILE_GONE) };
  const up = await deps.transport.upload({ op: 'document', lotId, stagedFileId, file: staged.file, blob: staged.blob });
  if (up.ok) return up;
  return { ok: false, outcome: uploadOutcome(up) };
}

/**
 * What a failed upload means for its entry: a 401 stops the pass with the entry pending, as a
 * send's 401 does (ADR 066 — Offline Mode § 4.6, BUG-2070); a server error or an expired upload URL
 * waits for a later pass; any other refusal fails it with the server's reason.
 */
function uploadOutcome(up: Exclude<ReplayUploadResult, { ok: true }>): ReplayOutcome {
  if (up.unauthorized) return { kind: 'transport', reason: 'unauthorized' };
  if (up.retryLater) return { kind: 'retry-later', error: { message: up.message, code: null, httpStatus: null, fieldNames: [] } };
  return failure(up.message);
}

/**
 * @param payload - a queued file add's payload.
 * @returns the staged files the entry owns: its file.
 */
export function stagedFilesOf(payload: ChangeQueuePayload): string[] {
  return payload.stagedFileId != null ? [payload.stagedFileId] : [];
}
